import { useCallback, useEffect, useState } from "react";
import { Phone, Film, Code2, CalendarClock, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const KIND = {
  phone: { icon: Phone, label: "Phone screen" },
  video: { icon: Film, label: "Video interview" },
  coding: { icon: Code2, label: "Coding round" },
  scheduled: { icon: CalendarClock, label: "Booked phone call" },
};

const mins = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)) : null);
const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function Row({ i }) {
  const K = KIND[i.kind];
  const when = i.kind === "scheduled"
    ? `Starts ${timeOf(i.scheduled_at)}`
    : `Running ${mins(i.started_at)} min${i.last_activity_at ? ` · last activity ${mins(i.last_activity_at)} min ago` : ""}`;
  return (
    <tr className="border-t border-slate-100" data-testid={`live-row-${i.kind}-${i.candidate_id}`}>
      <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-slate-700"><K.icon className="w-3.5 h-3.5 text-indigo-600" />{K.label}</span></td>
      <td className="px-4 py-2.5 font-medium text-slate-900">{i.candidate_name}</td>
      <td className="px-4 py-2.5 text-slate-600">{i.job_title}</td>
      <td className="px-4 py-2.5 text-slate-600">{i.workspace}</td>
      <td className="px-4 py-2.5 text-slate-500 text-xs">{when}{i.kind !== "scheduled" && i.status ? ` · ${i.status}` : ""}</td>
    </tr>
  );
}

export default function LiveInterviewsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/admin/live-interviews")).data); } catch { /* keep last */ } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const rows = data ? [...data.live, ...data.upcoming] : [];
  const safe = data?.safe_to_deploy;
  return (
    <div className="tp-card p-0 overflow-hidden" data-testid="live-interviews-panel">
      <div className={`flex items-center justify-between gap-3 flex-wrap px-5 py-4 ${!data ? "bg-slate-50" : safe ? "bg-emerald-50" : "bg-amber-50"}`}>
        <div className="flex items-center gap-3">
          {data && (safe ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <AlertTriangle className="w-6 h-6 text-amber-600" />)}
          <div>
            <div className="font-display font-semibold text-slate-900" data-testid="live-deploy-status">
              {!data ? "Checking live interviews…" : safe ? "Safe to deploy" : "Wait before deploying"}
            </div>
            <div className="text-xs text-slate-600" data-testid="live-summary">
              {data && `${data.live.length} in progress now · ${data.upcoming.length} phone call${data.upcoming.length === 1 ? "" : "s"} booked in the next 30 min · checked ${timeOf(data.checked_at)}`}
            </div>
          </div>
        </div>
        <button onClick={load} disabled={busy} data-testid="live-refresh" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2">Round</th><th className="px-4 py-2">Candidate</th><th className="px-4 py-2">Job</th><th className="px-4 py-2">Workspace</th><th className="px-4 py-2">Timing</th>
            </tr></thead>
            <tbody>{rows.map((i, n) => <Row key={`${i.kind}-${i.candidate_id}-${n}`} i={i} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
